import { Wallet } from 'lucide-react';
import { TextBadge } from '@/components/StatusBadge';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { SummaryStrip, type SummaryItem } from '@/components/drawer/SummaryStrip';
import { PdfDocumentRow } from '@/components/form/PdfDocumentRow';
import type { Loan, Policy } from '@/types';
import { formatCurrency, formatPaymentDate } from '@/lib/payments';
import { currentMonthKey, formatMonthKey, formatShortMonth } from '@/lib/months';
import { LOAN_MAX, LOAN_MIN_PAID_MONTHS, LOAN_MIN_PRINCIPAL, LOAN_RATE } from '@/lib/loans';

interface LoanStatusCardProps {
  policy: Policy;
  /** The open loan's full details, when the policy has one. */
  activeLoan?: Loan;
  closedLoans: Loan[];
}

const RATE_LABEL = `${LOAN_RATE * 100}% per year`;

/** Loan state of a policy. Same card in every state; only the badge and the four numbers change. */
export function LoanStatusCard({ policy, activeLoan, closedLoans }: LoanStatusCardProps) {
  const { loan } = policy;
  let title = 'Loan Status';
  let description: string;
  let badge: { tone: 'teal' | 'amber' | 'brand' | 'emerald' | 'gray'; text: string };
  let items: SummaryItem[];

  switch (loan.eligibility) {
    case 'active': {
      title = `Loan Status · ${loan.loanNo ?? ''}`;
      description = activeLoan
        ? `Given ${formatPaymentDate(activeLoan.givenOn)} · ${activeLoan.givenAs} · ${RATE_LABEL}`
        : RATE_LABEL;
      badge =
        loan.thisMonth === 'not_due'
          ? { tone: 'brand', text: `First Due ${formatShortMonth(activeLoan?.nextDueMonth)}` }
          : loan.thisMonth === 'paid'
            ? { tone: 'teal', text: `Paid for ${formatShortMonth(currentMonthKey())}` }
            : { tone: 'amber', text: `Due for ${formatShortMonth(activeLoan?.nextDueMonth ?? currentMonthKey())}` };
      items = [
        { label: 'Loan Amount', value: formatCurrency(loan.amount ?? 0) },
        { label: 'Principal Paid', value: formatCurrency(loan.principalPaid ?? 0), tone: 'text-teal-800' },
        { label: 'Outstanding', value: formatCurrency(loan.outstanding ?? 0), tone: 'text-amber-800' },
        { label: 'Interest Paid', value: formatCurrency(activeLoan?.interestPaid ?? 0) },
      ];
      break;
    }
    case 'eligible':
      description = 'No active loan on this policy';
      badge = { tone: 'emerald', text: 'Eligible' };
      items = [
        { label: 'Max Loan', value: formatCurrency(LOAN_MAX) },
        { label: 'Policy Paid', value: `${policy.paidMonths} / ${policy.totalMonths}` },
        { label: 'Interest', value: `${LOAN_RATE * 100}% / year` },
        { label: 'Min Repayment', value: `${formatCurrency(LOAN_MIN_PRINCIPAL)} / mo` },
      ];
      break;
    case 'waiting': {
      const lastLoan = closedLoans[0];
      description = lastLoan ? `Previous loan ${lastLoan.loanNo} closed this month` : 'Previous loan closed this month';
      badge = { tone: 'gray', text: `Available from ${formatShortMonth(loan.availableFrom)}` };
      items = [
        { label: 'Last Loan', value: lastLoan ? formatCurrency(lastLoan.amount) : '--' },
        { label: 'Closed', value: formatMonthKey(lastLoan?.closedMonth) },
        { label: 'Next Allowed', value: formatMonthKey(loan.availableFrom) },
        { label: 'Loans Taken', value: closedLoans.length },
      ];
      break;
    }
    case 'policy_closed':
      description = 'This policy is closed';
      badge = { tone: 'gray', text: 'Not Eligible' };
      items = [
        { label: 'Policy Paid', value: `${policy.paidMonths} / ${policy.totalMonths}` },
        { label: 'Loans Taken', value: closedLoans.length },
        { label: 'Status', value: 'Closed' },
        { label: 'Max Loan', value: '--' },
      ];
      break;
    default:
      description = 'Loans start from the 7th month of the policy';
      badge = { tone: 'gray', text: 'Not Eligible' };
      items = [
        { label: 'Policy Paid', value: `${policy.paidMonths} / ${LOAN_MIN_PAID_MONTHS}` },
        { label: 'Months Needed', value: loan.monthsNeeded ?? LOAN_MIN_PAID_MONTHS - policy.paidMonths },
        { label: 'Eligible From', value: formatMonthKey(loan.availableFrom) },
        { label: 'Max Loan', value: formatCurrency(LOAN_MAX) },
      ];
  }

  return (
    <DrawerSection
      icon={Wallet}
      title={title}
      description={description}
      aside={<TextBadge tone={badge.tone}>{badge.text}</TextBadge>}
    >
      <SummaryStrip items={items} />
      {loan.eligibility === 'active' && activeLoan?.agreementUrl && (
        <PdfDocumentRow
          name={`${activeLoan.loanNo}_agreement.pdf`}
          url={activeLoan.agreementUrl}
          caption="Signed loan agreement"
        />
      )}
    </DrawerSection>
  );
}
